import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useCart } from '../context/CartContext';
import { useStore } from '../context/StoreContext';
import { formatPrice } from '../lib/utils';
import {
  ArrowLeft,
  Scissors,
  Trash2,
  Plus,
  Minus,
  ShoppingBag
} from 'lucide-react';

export default function CartPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { store } = useStore();
  const { items, updateQuantity, removeItem, getTotal, clearCart } = useCart();

  const subtotal = getTotal();
  const tax = subtotal * 0.22;
  const total = subtotal + tax;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="glass fixed top-0 left-0 right-0 z-50">
        <div className="container-main flex items-center justify-between h-16">
          <div className="flex items-center gap-4">
            <Link to={`/s/${slug}/shop`} className="text-muted-foreground hover:text-foreground">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-2">
              <Scissors className="w-6 h-6 text-primary" />
              <span className="font-semibold">{store?.name} - Carrello</span>
            </div>
          </div>
        </div>
      </header>

      <div className="pt-24 pb-12 section-padding">
        <div className="container-main max-w-4xl">
          <h1 className="text-3xl font-bold mb-8">Il Tuo Carrello</h1>
          
          {items.length === 0 ? (
            <Card className="p-12 text-center">
              <ShoppingBag className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
              <h2 className="text-xl font-semibold mb-2">Carrello vuoto</h2>
              <p className="text-muted-foreground mb-6">
                Non hai ancora aggiunto prodotti al carrello.
              </p>
              <Button asChild>
                <Link to={`/s/${slug}/shop`}>Vai allo Shop</Link>
              </Button>
            </Card>
          ) : (
            <div className="grid lg:grid-cols-3 gap-8">
              {/* Cart Items */}
              <div className="lg:col-span-2 space-y-4">
                {items.map(({ product, quantity }) => (
                  <Card key={product.id} data-testid={`cart-item-${product.id}`}>
                    <CardContent className="p-4">
                      <div className="flex gap-4">
                        <div className="w-20 h-20 bg-muted rounded-lg flex items-center justify-center overflow-hidden">
                          {product.image_url ? (
                            <img 
                              src={product.image_url} 
                              alt={product.name}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <ShoppingBag className="w-8 h-8 text-muted-foreground/50" />
                          )}
                        </div>
                        
                        <div className="flex-1">
                          <h3 className="font-semibold">{product.name}</h3>
                          <p className="text-sm text-muted-foreground">
                            {formatPrice(product.price)} / unità
                          </p>
                          
                          <div className="flex items-center justify-between mt-3">
                            <div className="flex items-center gap-2">
                              <Button 
                                variant="outline" 
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => updateQuantity(product.id, quantity - 1)}
                                data-testid={`decrease-${product.id}`}
                              >
                                <Minus className="w-4 h-4" />
                              </Button>
                              <span className="w-8 text-center font-medium">{quantity}</span>
                              <Button 
                                variant="outline" 
                                size="icon"
                                className="h-8 w-8"
                                onClick={() => updateQuantity(product.id, quantity + 1)}
                                disabled={quantity >= product.stock}
                                data-testid={`increase-${product.id}`}
                              >
                                <Plus className="w-4 h-4" />
                              </Button>
                            </div>
                            
                            <div className="flex items-center gap-4">
                              <span className="font-semibold">
                                {formatPrice(product.price * quantity)}
                              </span>
                              <Button 
                                variant="ghost" 
                                size="icon"
                                className="text-destructive hover:text-destructive"
                                onClick={() => removeItem(product.id)}
                                data-testid={`remove-${product.id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
                
                <Button 
                  variant="outline" 
                  className="text-destructive hover:text-destructive"
                  onClick={clearCart}
                  data-testid="clear-cart"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  Svuota Carrello
                </Button>
              </div>
              
              {/* Order Summary */}
              <div>
                <Card className="sticky top-24">
                  <CardHeader>
                    <CardTitle>Riepilogo Ordine</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Subtotale</span>
                      <span>{formatPrice(subtotal)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">IVA (22%)</span>
                      <span>{formatPrice(tax)}</span>
                    </div>
                    <div className="border-t pt-4 flex justify-between">
                      <span className="font-semibold">Totale</span>
                      <span className="font-bold text-lg text-primary">{formatPrice(total)}</span>
                    </div>
                    
                    <Button 
                      className="w-full mt-4" 
                      size="lg"
                      onClick={() => navigate(`/s/${slug}/checkout`)}
                      data-testid="checkout-btn"
                    >
                      Procedi al Checkout
                    </Button>
                    
                    <Button 
                      variant="outline" 
                      className="w-full"
                      asChild
                    >
                      <Link to={`/s/${slug}/shop`}>
                        Continua lo Shopping
                      </Link>
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
